import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import { useQuery } from "@project/ui";
import { useI18n } from "@apps/dashboard/contexts/I18nContext";
import { fetchHouses, homeExpenseQueryKeys } from "@apps/dashboard/lib/home-expense-api";
import type { HouseListItem } from "@apps/dashboard/lib/home-expense-types";
import { trackDashboardEvent } from "@apps/dashboard/lib/analytics";

const STORAGE_KEY = "homeexpense:activeHouseId";

function readStoredHouseId(): string | null {
  try {
    const v = localStorage.getItem(STORAGE_KEY);
    return v && /^[0-9a-f-]{36}$/i.test(v) ? v : null;
  } catch {
    return null;
  }
}

interface ActiveHouseContextValue {
  houses: HouseListItem[] | undefined;
  housesLoading: boolean;
  housesError: boolean;
  refetchHouses: () => void;
  activeHouseId: string | null;
  activeHouse: HouseListItem | undefined;
  setActiveHouseId: (houseId: string) => void;
}

const ActiveHouseContext = createContext<ActiveHouseContextValue | null>(null);

export function ActiveHouseProvider({ children }: { children: ReactNode }): JSX.Element {
  const { locale } = useI18n();
  const [activeHouseId, setActiveHouseIdState] = useState<string | null>(() => readStoredHouseId());

  const {
    data: houses,
    isLoading: housesLoading,
    isError: housesError,
    refetch,
  } = useQuery({
    queryKey: homeExpenseQueryKeys.houses(locale),
    queryFn: () => fetchHouses(locale),
  });

  useEffect(() => {
    if (!houses) {
      return;
    }
    if (houses.length === 0) {
      setActiveHouseIdState(null);
      try {
        localStorage.removeItem(STORAGE_KEY);
      } catch {
        /* ignore */
      }
      return;
    }
    setActiveHouseIdState((current) => {
      if (current && houses.some((h) => h.id === current)) {
        return current;
      }
      const next = houses[0]!.id;
      try {
        localStorage.setItem(STORAGE_KEY, next);
      } catch {
        /* ignore */
      }
      return next;
    });
  }, [houses]);

  const setActiveHouseId = useCallback((houseId: string) => {
    setActiveHouseIdState(houseId);
    void trackDashboardEvent("house_selected", { platform: "web" });
    try {
      localStorage.setItem(STORAGE_KEY, houseId);
    } catch {
      /* ignore */
    }
  }, []);

  const activeHouse = useMemo(
    () => houses?.find((h) => h.id === activeHouseId),
    [houses, activeHouseId],
  );

  const value = useMemo<ActiveHouseContextValue>(
    () => ({
      houses,
      housesLoading,
      housesError,
      refetchHouses: () => {
        void refetch();
      },
      activeHouseId,
      activeHouse,
      setActiveHouseId,
    }),
    [
      houses,
      housesLoading,
      housesError,
      refetch,
      activeHouseId,
      activeHouse,
      setActiveHouseId,
    ],
  );

  return <ActiveHouseContext.Provider value={value}>{children}</ActiveHouseContext.Provider>;
}

export function useActiveHouse(): ActiveHouseContextValue {
  const ctx = useContext(ActiveHouseContext);
  if (!ctx) {
    throw new Error("useActiveHouse must be used within ActiveHouseProvider");
  }
  return ctx;
}
